import type { TaskTemplateInput } from "@/schema/task-template"
import { useMutation, useQueryClient } from "@tanstack/react-query"

import { useApiContext } from "@/hooks/api/use-api-context"

import {
  cloneCompanyTaskTemplate,
  createCompanyTaskTemplate,
  destroyCompanyTaskTemplate,
  updateCompanyTaskTemplate,
} from "./api"

export function useCreateCompanyTaskTemplate() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ taskTemplate, scope }: { taskTemplate: TaskTemplateInput; scope?: "company" | "book" }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return createCompanyTaskTemplate(context, taskTemplate, scope ? { scope } : undefined)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["company-task-templates"] })
    },
  })
}

export function useUpdateCompanyTaskTemplate() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, taskTemplate }: { id: string; taskTemplate: Partial<TaskTemplateInput> }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return updateCompanyTaskTemplate(context, id, taskTemplate)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["company-task-templates"] })
    },
  })
}

export function useDestroyCompanyTaskTemplate() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return destroyCompanyTaskTemplate(context, id)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["company-task-templates"] })
    },
  })
}

// Alias for consistency with naming conventions
export const useDeleteCompanyTaskTemplate = useDestroyCompanyTaskTemplate

export function useCloneCompanyTaskTemplate() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, bookId }: { id: string; bookId?: string }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return cloneCompanyTaskTemplate(context, id, bookId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["company-task-templates"] })
    },
  })
}
